import React from 'react';
import { useParams } from 'react-router-dom';
import { TableData } from '@/components';
import { Column } from '@/types/common';
import { OutboundOrderLine, warehouseApi } from '@/services/api/warehouse';
import {
  CARRIER_LABEL,
  codeCls,
  DetailHead,
  DetailSkeleton,
  fmtDate,
  fmtDateTime,
  fmtQty,
  FULFILLMENT_STATUS,
  hubName,
  KV,
  KVGrid,
  labelOf,
  LoadError,
  metaCls,
  METHOD_LABEL,
  Notice,
  numCls,
  OUTBOUND_SOURCE_LABEL,
  OUTBOUND_STATUS,
  productNameCls,
  Section,
  SERVICE_LABEL,
  StatusBadge,
  Tag,
  Timeline,
  useLoad,
  WAREHOUSE_PATHS,
} from './shared';

const LINE_COLUMNS: Column<OutboundOrderLine>[] = [
  {
    key: 'sku',
    title: 'SKU',
    render: (_v, l) => (
      <span className={`${codeCls} block max-w-[150px] truncate`} title={l.sku}>
        {l.sku}
      </span>
    ),
  },
  {
    key: 'itemName',
    title: 'Product name',
    render: (_v, l) => (
      <span className={productNameCls} title={l.itemName}>
        {l.itemName}
      </span>
    ),
  },
  {
    key: 'qty',
    title: 'Order',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qty)}</span>,
  },
  {
    key: 'qtyReserved',
    title: 'Reserved',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qtyReserved)}</span>,
  },
  {
    key: 'qtyPicked',
    title: 'Picked',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qtyPicked)}</span>,
  },
  {
    key: 'uomName',
    title: 'UoM',
    render: (_v, l) => <span className={metaCls}>{l.uomName}</span>,
  },
];

const OutboundDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const detail = useLoad(() => warehouseApi.getOutboundOrder(id), [id]);

  if (detail.loading && !detail.data) return <DetailSkeleton />;
  if (detail.error || !detail.data) {
    return (
      <div className="space-y-5 p-4 sm:p-6">
        <DetailHead
          backTo={WAREHOUSE_PATHS.outbound}
          backLabel="Outbound request"
          title={id}
        />
        <div className="rounded-xl border border-stroke-soft-200 bg-bg-white-0">
          <LoadError
            message={detail.error ?? 'Outbound request tidak ditemukan'}
            onRetry={detail.reload}
          />
        </div>
      </div>
    );
  }

  const { order: o, lines, events } = detail.data;
  const f = o.fulfillment;

  return (
    <div className="space-y-5 p-4 sm:p-6">
      <DetailHead
        backTo={WAREHOUSE_PATHS.outbound}
        backLabel="Outbound request"
        title={o.code}
        badges={
          <>
            <StatusBadge map={OUTBOUND_STATUS} value={o.status} />
            <Tag>{labelOf(OUTBOUND_SOURCE_LABEL, o.source)}</Tag>
          </>
        }
        meta={`Dibuat ${fmtDateTime(o.createdAt)}`}
      />

      {o.status === 'CANCELLED' && (
        <Notice title="Order dibatalkan">
          {o.cancellationReason || '—'} · {fmtDateTime(o.cancelledAt)}
        </Notice>
      )}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="flex flex-col gap-5 xl:col-span-2">
          <Section title="Detail order">
            <KVGrid
              items={[
                ['Request ID', <span className={codeCls}>{o.code}</span>],
                [
                  'External ID',
                  <span className={codeCls}>{o.externalID}</span>,
                ],
                ['Order date', fmtDateTime(o.orderDate)],
                ['Expected delivery', fmtDate(o.expectedDeliveryDate)],
                ['Method', labelOf(METHOD_LABEL, o.method)],
                ['Service', labelOf(SERVICE_LABEL, o.serviceType)],
                ['Catatan order', o.notes || null],
              ]}
            />
          </Section>
          <Section title="Products" flush>
            <TableData<OutboundOrderLine>
              data={lines}
              columns={LINE_COLUMNS}
              size="small"
              className="rounded-none border-0 shadow-none"
            />
          </Section>
          <Section title="Riwayat">
            <Timeline
              entries={events.map((e) => ({
                id: e.id,
                at: e.occurredAt,
                title: e.title,
                note: e.note || undefined,
                badge: e.status ? (
                  <StatusBadge map={OUTBOUND_STATUS} value={e.status} />
                ) : undefined,
              }))}
            />
          </Section>
        </div>

        <div className="flex flex-col gap-5">
          {(f?.waybill || f?.status || o.courier) && (
            <Section title="Pengiriman">
              {f?.carrier === 'HANDOVER' ? (
                // Handed over at the hub (or carried to the destination hub):
                // by design there is no shipment, waybill or courier.
                <KV
                  label="Metode pengiriman"
                  value={CARRIER_LABEL.HANDOVER}
                  sub="Barang diserahkan langsung — tanpa kurir dan waybill. Penerima ada di Riwayat."
                />
              ) : (
                <KV
                  label="Shipment (Waybill)"
                  value={
                    f?.waybill ? (
                      <span className={codeCls}>{f.waybill}</span>
                    ) : null
                  }
                />
              )}
              {f?.status && (
                <KV
                  label="Status shipment"
                  value={
                    <StatusBadge map={FULFILLMENT_STATUS} value={f.status} />
                  }
                />
              )}
              {f?.carrier !== 'HANDOVER' && (
                <KV
                  label="Kurir"
                  value={
                    o.courier?.name ??
                    (f?.carrier && f.carrier !== 'DASH'
                      ? labelOf(CARRIER_LABEL, f.carrier)
                      : null)
                  }
                  sub={
                    o.courier
                      ? `${o.courier.code} · ID ${o.courierID}`
                      : f?.carrier && f.carrier !== 'DASH'
                        ? 'Kurir pihak ketiga'
                        : 'Belum ada kurir'
                  }
                />
              )}
              {o.courier?.phone && (
                <KV label="Telepon kurir" value={o.courier.phone} />
              )}
            </Section>
          )}
          <Section title="Origin">
            <KV
              label="Hub Name"
              value={hubName(o.hub, o.hubID)}
              sub={o.hub?.code ?? undefined}
            />
          </Section>
          <Section title="Destination">
            {o.type === 'TRANSFER' && (
              <KV label="Hub tujuan" value={o.destinationHub?.name ?? null} />
            )}
            <KV label="Penerima" value={o.recipientName} />
            <KV label="Telepon" value={o.recipientPhone} />
            <KV label="Alamat" value={o.destinationAddress} />
            {o.type !== 'TRANSFER' && (
              <KV label="Kota" value={o.destinationCity} />
            )}
            {o.destinationNotes && (
              <KV label="Catatan" value={o.destinationNotes} />
            )}
          </Section>
        </div>
      </div>
    </div>
  );
};

export default OutboundDetailPage;
